/**
 * Qué archivos entran a la Memoria del cliente — la ÚNICA fuente de verdad del frontend.
 *
 * La consumen el hook de ingesta del Recorrido (`ia_booster/useMemoryIngest`), la Barra Total, el
 * composer, el orbe y la vista clásica «Cargar» (`MemorySetupView`). Antes cada puerta tenía su
 * propio `accept` y su propio lector: extender los formatos en una y no en otra dejaba una puerta
 * con PDF permitido y otra que lo rechazaba.
 *
 * Dos modos de transporte:
 *  - `text`   → el archivo se lee en el navegador (`FileReader.readAsText`) y viaja como `raw_text`
 *               por el POST JSON de siempre. Sólo para lo que ES texto: un PDF leído así entra como
 *               bytes ilegibles y el extractor se lo come sin fallar.
 *  - `upload` → el binario viaja por multipart (`utils/multipart.js`) y el backend lo guarda en su
 *               bucket, lo sniffea por magic bytes y lo lee con el mismo pipeline que Drive.
 *
 * Sin React ni i18n: sólo devuelve KEYS. Los textos los resuelve quien muestra el rechazo.
 */

export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
export const MAX_AUDIO_UPLOAD_BYTES = 25 * 1024 * 1024;
// Cuántas subidas binarias viajan a la vez. Seis XHR de 20 MB en paralelo sobre una conexión lenta
// hacen que NINGUNO termine pronto; con dos, la primera cierra en segundos y el usuario ve movimiento.
export const UPLOAD_CONCURRENCY = 2;

export const FILE_KIND = {
    TEXT: 'text', PDF: 'pdf', DOCX: 'docx', IMAGE: 'image', AUDIO: 'audio',
};
export const INGEST_MODE = {TEXT: 'text', UPLOAD: 'upload', REJECTED: 'rejected'};

// La key que enumera los formatos, interpolada como `{{formats}}` en los cuatro textos que los
// nombran (hint de la barra, hint del composer, «o elige un archivo» del Setup, rechazo genérico).
// Una sola key para que no diverjan. ⚠️ Los textos que la usan van con
// `interpolation: {escapeValue: false}`, y no por esta key sino por `{{name}}`: el nombre de un
// archivo trae comillas, `&` y acentos, que i18next escaparía a entidades HTML.
export const FORMATS_KEY = 'client_memory.upload.formats';

// Tabla de admisión. La extensión manda (un `File` de Windows puede venir con `type` vacío); el
// MIME es el fallback para archivos sin extensión. Allowlist, NUNCA `type.startsWith('text/')`:
// ese prefijo deja entrar `text/html`, `text/csv` y `text/xml`, que son texto para el navegador y
// no para el extractor.
const ACCEPTED = [
    // El `.csv` va por el camino de TEXTO igual que el `.txt`: el backend lo acepta
    // (`upload_admission._TEXT_EXTENSIONS`) y Drive lo lee desde siempre (`PLAIN_TEXT_MIMES`).
    // Estuvo un tiempo en los rechazos "es una planilla": era la única divergencia entre las dos
    // puntas de la tabla, y la que estaba mal era ésta. Un `.xlsx` sí se rechaza — ése no es texto.
    {kind: FILE_KIND.TEXT, mode: INGEST_MODE.TEXT, ext: ['txt', 'vtt', 'md', 'markdown', 'csv'],
        mime: ['text/plain', 'text/markdown', 'text/vtt', 'text/csv']},
    // El `.html` es texto pero va por el camino de SUBIDA, y la diferencia no es un detalle: por
    // multipart el backend le saca el markup con su conversor (`MaterialParser` → `html_to_text`,
    // el mismo que usa una URL pegada), mientras que leerlo acá con `FileReader` mandaría los tags
    // y las hojas de estilo como si fueran el documento. Entra desde el 2026-09-13: es el formato
    // con el que un cliente reenvía un informe desde el mail.
    {kind: FILE_KIND.TEXT, mode: INGEST_MODE.UPLOAD, ext: ['html', 'htm'], mime: ['text/html']},
    {kind: FILE_KIND.PDF, mode: INGEST_MODE.UPLOAD, ext: ['pdf'], mime: ['application/pdf']},
    {kind: FILE_KIND.DOCX, mode: INGEST_MODE.UPLOAD, ext: ['docx'],
        mime: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document']},
    // GIF NO: Vertex lo rechaza y el documento entero falla con un error opaco.
    {kind: FILE_KIND.IMAGE, mode: INGEST_MODE.UPLOAD, ext: ['png', 'jpg', 'jpeg', 'webp'],
        mime: ['image/png', 'image/jpeg', 'image/webp']},
    // Lo que graba `MediaRecorder` (webm en Chrome, ogg en Firefox, mp4 en Safari) más lo que una
    // agencia tiene a mano. `video/webm` NO está: una grabación de pantalla renombrada la decide
    // el backend por magic bytes.
    {kind: FILE_KIND.AUDIO, mode: INGEST_MODE.UPLOAD, ext: ['webm', 'ogg', 'wav', 'mp3', 'm4a'],
        mime: ['audio/webm', 'audio/ogg', 'audio/wav', 'audio/x-wav', 'audio/mpeg', 'audio/mp4',
            'audio/x-m4a']},
];

// Rechazos con pista. Sin fila acá el rechazo es el genérico, que nombra el archivo y lista lo que
// sí entra. La pista importa: «no se puede leer» a secas deja al usuario probando con otro .xlsx.
const REJECT_HINTS = {
    xlsx: 'spreadsheet', xls: 'spreadsheet', numbers: 'spreadsheet',
    ods: 'spreadsheet',
    pptx: 'export_to_pdf', ppt: 'export_to_pdf', key: 'export_to_pdf', odp: 'export_to_pdf',
    doc: 'export_to_pdf', odt: 'export_to_pdf', rtf: 'export_to_pdf', pages: 'export_to_pdf',
    gif: 'gif',
};

const ERROR_NS = 'client_memory.upload_error';
export const uploadErrorKey = (reason) => `${ERROR_NS}.${reason}`;

// El `accept` del input, derivado de la tabla: el selector de archivos ofrece EXACTAMENTE lo que
// el gate acepta. Si divergen, deja elegir algo que después se rechaza.
export const INGEST_ACCEPT = ACCEPTED
    .flatMap(row => row.ext.map(ext => `.${ext}`).concat(row.mime))
    .join(',');

const extensionOf = (name) => {
    const match = /\.([a-z0-9]+)$/i.exec(String(name || ''));
    return match ? match[1].toLowerCase() : '';
};

// `audio/webm;codecs=opus` es lo que dice el blob de Chrome: el parámetro se descarta.
const normalizeMime = (type) => String(type || '').split(';')[0].trim().toLowerCase();

const rowFor = (file) => {
    const ext = extensionOf(file.name);
    if (ext) {
        const byExt = ACCEPTED.find(row => row.ext.includes(ext));
        if (byExt) return byExt;
    }
    const mime = normalizeMime(file.type);
    return mime ? ACCEPTED.find(row => row.mime.includes(mime)) || null : null;
};

export const maxBytesFor = (kind) => (kind === FILE_KIND.AUDIO ? MAX_AUDIO_UPLOAD_BYTES : MAX_UPLOAD_BYTES);

const rejected = (file, reason, extra = {}) => ({
    mode: INGEST_MODE.REJECTED,
    kind: null,
    reasonKey: uploadErrorKey(reason),
    reasonParams: {name: (file && file.name) || '', ...extra},
});

/**
 * Clasifica un `File`: `{mode, kind}` si entra, `{mode: 'rejected', reasonKey, reasonParams}` si no.
 *
 * Orden: vacío → tabla (extensión, luego MIME) → tamaño con el tope de SU familia → pista por
 * extensión → genérico. El tamaño se chequea también para el texto: un `.txt` de 25 MB se leería
 * entero a memoria para recortarlo a 200k caracteres.
 */
export function classifyFile(file) {
    if (!file) return rejected(file, 'unsupported_type');
    if (file.size === 0) return rejected(file, 'empty_file');
    const row = rowFor(file);
    if (row) {
        const max = maxBytesFor(row.kind);
        if (file.size > max) {
            return rejected(file, 'too_large', {max: Math.round(max / (1024 * 1024))});
        }
        return {mode: row.mode, kind: row.kind};
    }
    const hint = REJECT_HINTS[extensionOf(file.name)];
    return rejected(file, hint || 'unsupported_type');
}

export const isReadableAsText = (file) => classifyFile(file).mode === INGEST_MODE.TEXT;

/** El `kind` de un archivo YA cargado (listado): por nombre y MIME, sin mirar tamaño. */
export function fileKindOf(name, type) {
    const row = rowFor({name, type});
    return row ? row.kind : null;
}

// Ícono FontAwesome (SVG, `FaIcon`) por tipo. Los cuatro nuevos están en `init_icons_library`.
const FA_ICON = {
    [FILE_KIND.PDF]: 'file-pdf',
    [FILE_KIND.DOCX]: 'file-word',
    [FILE_KIND.IMAGE]: 'file-image',
    [FILE_KIND.AUDIO]: 'file-audio',
    [FILE_KIND.TEXT]: 'file-lines',
};
export const fileKindFaIcon = (kind) => FA_ICON[kind] || FA_ICON[FILE_KIND.TEXT];

/** «2,3 MB» / «480 KB». Locale del navegador para el separador decimal. */
export function formatBytes(bytes) {
    const n = Number(bytes) || 0;
    if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
    const mb = n / (1024 * 1024);
    return `${mb.toLocaleString(undefined, {maximumFractionDigits: 1})} MB`;
}

/**
 * Corre `fn` sobre cada ítem con a lo sumo `limit` en vuelo, conservando el ORDEN de los resultados.
 * Devuelve `Promise<Array<{item, ok}>>`; un `fn` que tira cuenta como `ok: false`.
 */
export function runLimited(items, limit, fn) {
    const list = [...items];
    const results = new Array(list.length);
    let next = 0;
    const worker = () => {
        if (next >= list.length) return Promise.resolve();
        const index = next;
        next += 1;
        const item = list[index];
        return Promise.resolve()
            .then(() => fn(item))
            .then((ok) => { results[index] = {item, ok: ok === true}; },
                () => { results[index] = {item, ok: false}; })
            .then(worker);
    };
    const workers = [];
    for (let i = 0; i < Math.max(1, Math.min(limit, list.length)); i += 1) workers.push(worker());
    return Promise.all(workers).then(() => results);
}

// Cuánto esperar los metadatos del audio antes de rendirse. Es un HINT para el backend (el cap
// duro lo pone él): un `null` no bloquea la subida.
const AUDIO_DURATION_TIMEOUT_MS = 3000;

/** Duración de un archivo de audio en segundos, o `null` si el navegador no la sabe. Nunca rechaza. */
export function readAudioDuration(file) {
    if (typeof document === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL) {
        return Promise.resolve(null);
    }
    return new Promise((resolve) => {
        let url = null;
        let settled = false;
        const finish = (value) => {
            if (settled) return;
            settled = true;
            if (url) { try { URL.revokeObjectURL(url); } catch (_) { /* no-op */ } }
            resolve(value);
        };
        try {
            const audio = document.createElement('audio');
            audio.preload = 'metadata';
            audio.onloadedmetadata = () => {
                const seconds = audio.duration;
                finish(Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds) : null);
            };
            audio.onerror = () => finish(null);
            url = URL.createObjectURL(file);
            audio.src = url;
            setTimeout(() => finish(null), AUDIO_DURATION_TIMEOUT_MS);
        } catch (_) {
            finish(null);
        }
    });
}
